import { AxiosResponse } from 'axios';
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import isString from 'lodash/isString';

import { FEATURE_FLAG } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import {
  Assignee,
  ExecutionHistoryDetail,
  ExecutionStatus,
  HistoryStep,
  TestCaseInCycle,
  TestExecution,
  User,
} from '@qality/common-model';
import { Issue } from '@qality/common-model/src/Issue';
import { LastExecution } from '@qality/common-model/src/LastExecution';
import {
  StorageOfUniqueServices,
  AxiosWrapperService as axios,
} from '@qality/common-service';
import { StringUtils, Utils } from '@qality/common-utils';

import IssueService from './IssueService';
import UserService from './UserService';

const TEST_EXECUTION_NAME_LIMIT = 255;
const TEST_EXECUTION_NAME_SUFFIX = ' Test Case Execution';

export default {
  async createTestExecution(
    testCaseName: string,
    testCaseId: string | number,
    testCaseInCycleId?: number,
    executionAssignee?: User
  ): Promise<TestExecution> {
    const testExecutionName = generateTestExecutionName(testCaseName);

    const testSteps = [];

    const requestData = new TestExecution(
      testExecutionName,
      testCaseId,
      testSteps,
      null,
      Date.now(),
      testCaseInCycleId,
      executionAssignee
    ).getCreateRequestData();

    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION
    );
    const response = await axios.post(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  },

  async bulkCreateTestExecution(
    testCasesToExecute: Array<TestCaseInCycle>,
    fields: {
      assignee?: Assignee;
      executionStatus?: ExecutionStatus;
    }
  ): Promise<Array<TestExecution>> {
    const newTestExecutions = testCasesToExecute.map((testCase) => {
      const testExecutionName = generateTestExecutionName(
        testCase.testCase.fields.summary
      );

      const newTestExecution = {
        name: testExecutionName,
        comment: '',
        testCaseId: testCase.testCaseId.toString(),
        // TODO: replace statusObject with status new model as status
        statusObject: fields.executionStatus,
        executionAssignee: fields.assignee?.user?.getIdentifier(),
        testCaseInCycleId: testCase.id,
      };

      return newTestExecution;
    });

    const requestData = { testExecutions: newTestExecutions };

    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION_BULK
    );
    const response = await axios.post(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  },

  async updateTestExecutionSteps(
    testExecutionSteps: TestExecution['testSteps']
  ): Promise<AxiosResponse> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants')
          .URL_TEST_EXECUTION_STEPS_BULK
    );

    const requestData = testExecutionSteps.map((testExecutionStep) => {
      const stepAttachements = JSON.stringify(
        testExecutionStep.executionAttachments.map((attachment) => {
          return { id: attachment.id.toString() };
        })
      );

      return {
        id: testExecutionStep.testExecutionStepId,
        fields: {
          statusId: testExecutionStep.status.id,
          comment: testExecutionStep.comment,
          attachments: stepAttachements,
        },
      };
    });

    return axios.patch(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  async updateTestExecution(
    testExecution: TestExecution,
    shouldHandleError = false
  ): Promise<AxiosResponse> {
    try {
      const url = await StorageOfUniqueServices.get(
        'AuthenticateService'
      ).authenticateByJWT(
        StorageOfUniqueServices.get('WindowOriginService').origin +
          StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION +
          `/${testExecution.id}`
      );

      const promise = axios.patch(url, testExecution.getUpdateRequestData(), {
        headers: {
          'Content-Type': 'application/json',
        },
      });
      if (shouldHandleError) {
        return await promise;
      } else {
        return promise;
      }
    } catch (e) {
      if (shouldHandleError) {
        StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
          e,
          messages['error.testCaseExecutionPage.updateTestExecution.message']
        );
      } else {
        throw e;
      }
    }
  },

  async updateExecutionAssignee(
    testExecutionId: number,
    executionAssigneeId: string = null
  ): Promise<TestExecution> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION +
        `/${testExecutionId}`
    );

    const requestData = {
      id: testExecutionId,
      fields: { executionAssignee: executionAssigneeId },
    };

    const response = await axios.patch(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
    return response.data;
  },

  async bulkUpdateExecutionsAssignee(requestData: {
    testExecutions: Array<{
      testExecutionId: number;
      executionAssignee: string;
    }>;
  }): Promise<TestExecution> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION_ASSIGNEE
    );

    const response = await axios.put(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });

    return response.data;
  },

  async bulkUpdateExecutionsStatus(requestData: {
    testExecutions: Array<{
      executionStatus: ExecutionStatus;
      testExecutionId: number;
    }>;
  }): Promise<void> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION_STATUS
    );

    await axios.put(url, requestData, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  async fetchTestExecution(testExecutionId: number): Promise<TestExecution> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION +
        '/' +
        testExecutionId
    );
    const response = await axios.get(url);
    return await convertObjectToTestExecution(response.data);
  },

  async fetchTestExecutionHistory(
    testCaseId: number,
    nextPage: number
  ): Promise<{
    pagination: {
      currentPage: number;
      pageSize: number;
      totalPages: number;
    };
    historyRows: HistoryStep[];
  }> {
    const response = await getExecutionHistoryData(testCaseId, nextPage);

    const { users, issues } = await fetchUsersAndBugsData(response);

    const historySteps = generateHistorySteps(response, users, issues);

    return {
      pagination: {
        currentPage: get(
          response,
          'pageable.pageNumber',
          nextPage ? nextPage : 0
        ),
        pageSize: get(response, 'pageable.pageSize', 10),
        totalPages: get(response, 'totalPages', 1),
      },
      historyRows: historySteps,
    };
  },

  async deleteTestExecution(testExecutionId: number): Promise<AxiosResponse> {
    const link =
      StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION +
      '/' +
      testExecutionId;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin + link
    );
    return axios.delete(url);
  },

  async getTestExecutionHistoryDetails(
    testCaseId: number
  ): Promise<ExecutionHistoryDetail> {
    const link =
      StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION +
      `/history/${testCaseId.toString()}`;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin + link
    );
    return axios.get(url).then((res) => res.data);
  },

  async getLastTestExecutionData(
    testCaseIds: Array<string>
  ): Promise<Array<LastExecution>> {
    if (!FEATURE_FLAG.isPremium) {
      return Promise.resolve([]);
    }

    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_LATEST_TEST_EXECUTIONS
    );

    const response = await axios.post(
      url,
      { testCaseIds },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      }
    );

    return response.data;
  },

  async getTestExecutionReportedBugs(
    testCaseId: number
  ): Promise<AxiosResponse> {
    const link =
      StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION_INSIGHTS +
      '/reportedBugs';
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin + link,
      'testCaseId=' + testCaseId
    );
    return axios.get(url);
  },

  async getTestExecutionFailedSteps(
    testCaseId: number
  ): Promise<AxiosResponse> {
    const link =
      StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION_INSIGHTS +
      '/failedSteps';
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin + link,
      'testCaseId=' + testCaseId
    );
    return axios.get(url);
  },
};

function generateTestExecutionName(testCaseName: string): string {
  const tmpName = testCaseName.replace(/ test case$/g, '');
  const name = StringUtils.truncate(
    tmpName,
    TEST_EXECUTION_NAME_LIMIT - TEST_EXECUTION_NAME_SUFFIX.length
  );

  return name + TEST_EXECUTION_NAME_SUFFIX;
}

async function getExecutionHistoryData(testCaseId: number, nextPage: number) {
  const url = await StorageOfUniqueServices.get(
    'AuthenticateService'
  ).authenticateByJWT(
    StorageOfUniqueServices.get('WindowOriginService').origin +
      StorageOfUniqueServices.get('APIConstants').URL_TEST_EXECUTION,
    `testCaseId=${testCaseId}&page=${nextPage}`
  );

  return (await axios.get(url)).data;
}

async function fetchUsersAndBugsData(executionHistoryData: any): Promise<{
  users: { [key: number]: User };
  issues: { [key: number]: Issue };
}> {
  const usersData = [];
  const users = {};
  const issues = {};

  let issuesIds = [];

  executionHistoryData.content.forEach((testExecution) => {
    if (testExecution.executionAssignee) {
      usersData.push(UserService.getUserById(testExecution.executionAssignee));
    }

    testExecution.testSteps.forEach((step) => {
      issuesIds = issuesIds.concat(step.linkedIssues);
    });
  });

  const usersArray = await Promise.all(usersData);
  const issuesArray = await StorageOfUniqueServices.get(
    'JQLSearchService'
  ).bulkFetchIssuesByIds({
    ids: issuesIds.map((issue) => issue.linkedIssueId),
    maxResults: 50,
    fields: ['summary', 'issuetype', 'status'],
  });

  usersArray.forEach((dataItem) => {
    if (dataItem) {
      users[dataItem.getIdentifier()] = dataItem;
    }
  });

  issuesArray.forEach((issue) => {
    issues[issue.id] = issue;
  });

  return { users, issues };
}

function generateHistorySteps(
  executionHistoryData: any,
  users: { [key: number]: User },
  issues: { [key: number]: Issue }
): Array<HistoryStep> {
  return executionHistoryData.content.map((testExecution) => {
    let linkedBugsIds = [];

    testExecution.testSteps.forEach((step) => {
      linkedBugsIds = linkedBugsIds.concat(step.linkedIssues);
    });

    linkedBugsIds = linkedBugsIds.map((issue) => issue.linkedIssueId);

    return new HistoryStep(
      testExecution.createdDate,
      // TODO replace statusObject with status after backend update
      testExecution.statusObject,
      testExecution.comment,
      testExecution.id,
      testExecution.testCaseInCycle,
      UserService.getAssignee(testExecution.executionAssignee, users),
      IssueService.getIssuesFromList(linkedBugsIds, issues)
    );
  });
}

async function convertObjectToTestExecution(responseObject: any) {
  const testSteps = get(responseObject, 'version.testSteps').map((testStep) => {
    return {
      ...testStep,
      attachments: getAttachments(testStep),
    };
  });
  const executionAssignee = await UserService.fetchAssignee(
    responseObject.executionAssignee
  );
  const testCaseIssue = await IssueService.getIssue(
    responseObject.testCaseId,
    true
  );

  if (responseObject.nextTestCase) {
    responseObject.nextTestCase.status =
      responseObject.nextTestCase.statusObject; // TODO: remove after status cleanup
  }

  if (responseObject.previousTestCase) {
    responseObject.previousTestCase.status =
      responseObject.previousTestCase.statusObject; // TODO: remove after status cleanup
  }

  const testExecution = new TestExecution(
    responseObject.name,
    responseObject.testCaseId,
    testSteps,
    responseObject.statusObject, // TODO: replace with status after cleanup
    responseObject.createdDate,
    undefined,
    executionAssignee,
    {
      next: responseObject.nextTestCase,
      prev: responseObject.previousTestCase,
    },
    responseObject.testType
  );
  testExecution.comment = responseObject.comment;
  testExecution.id = responseObject.id;
  testExecution.testCaseIssue = testCaseIssue;
  testExecution.testCycle = responseObject.testCaseInCycle
    ? responseObject.testCaseInCycle.testCycle
    : null;
  if (!Utils.isNilOrEmpty(responseObject.testSteps)) {
    testExecution.testSteps = testExecution.testSteps.map((testStep) => {
      const updatedTestStep = cloneDeep(testStep);
      const executionTestStep = responseObject.testSteps.find(
        (executionTestStepParam) =>
          executionTestStepParam.testCaseStepId === testStep.id
      );

      updatedTestStep.status = executionTestStep.statusObject; // TODo: replace with status
      updatedTestStep.comment = executionTestStep.comment;
      updatedTestStep.linkedIssues = executionTestStep.linkedIssues;
      updatedTestStep.testExecutionStepId = executionTestStep.id;
      updatedTestStep.executionAttachments = getAttachments(executionTestStep);

      return updatedTestStep;
    });
  }

  return testExecution;
}

function getAttachments(testStep) {
  const updatedTestStep = cloneDeep(testStep);
  if (isString(testStep.attachments)) {
    return JSON.parse(testStep.attachments);
  } else if (Utils.isNilOrEmpty(testStep.attachments)) {
    return [];
  }
  return updatedTestStep;
}
